import {NgModule} from '@angular/core';
import {RouterModule, Routes} from '@angular/router';
import {LoginComponent} from './login/login/login.component';
import {TokenLoginComponent} from './login/token-login/token-login.component';
import {AuthGuard} from './core/guards/auth.guard';
import {DefaultLayoutComponent} from './core/layouts/default-layout/default-layout.component';
import {LoginLayoutComponent} from './core/layouts/login-layout/login-layout.component';
import {Feature} from './core/constants/feature-constants';
import {FeatureSupportedGuard} from './core/guards/feature-supported.guard';
import {ParentsLayoutComponent} from '@app/core/layouts/parents-layout/parents-layout.component';
import {NosidebarLayoutComponent} from '@app/core/layouts/nosidebar-layout/nosidebar-layout.component';
import {LicenceGuard} from '@app/core/guards/licence.guard';
import {RoleAccessGuard} from './core/guards/role-access.guard';
import {UserRole} from './core/auth/auth.service';
import {LogoutComponent} from '@app/login/logout/logout/logout.component';
import {OptionalHeaderLayoutComponent} from '@app/core/layouts/optional-header-layout/optional-header-layout.component';


const routes: Routes = [
    /*
     * Global query params:
     * - lang: 'de'|'en' - Language (optional)
     */

  // "Default route"
  { path: '', redirectTo: '/dashboard', pathMatch: 'full' },
  // All routes that require auth, require the default role, require a licence and use the DefaultLayoutComponent
  {
    path: '',
    canActivate: [AuthGuard, RoleAccessGuard, LicenceGuard, FeatureSupportedGuard],
    canActivateChild: [AuthGuard, RoleAccessGuard, LicenceGuard, FeatureSupportedGuard],
    canLoad: [FeatureSupportedGuard],
    data: {
      roles: [UserRole.Default, UserRole.Parents]
    },
    component: DefaultLayoutComponent,
    children: [
      /**
       * Every child route may define an array of features of which at least one must be supported
       * by the active device to allow loading the route (if the route is already loaded and the device is changed,
       * a redirect to the dashboard will be performed if the features are not supported).
       *
       * Child routes of these routes can also define features (i.e. the routes defined in the other modules). Two cases are possible:
       * - The child route defines at least one feature: At least one of the features must be supported
       * - The child route does not define a feature: All parent routes are checked for supported features
       *
       * If no route defines a feature, the route is allowed by default.
       *
       * Example:
       * ```typescript
       * {
       *   path: 'gps',
       *   loadChildren: () => import('@app/gps/gps.module').then(m => m.GpsModule),
       *
       *   // Recommended if features are defined for the route (prevents lazy loading if route is not allowed)
       *   canLoad: [FeatureSupportedGuard],
       *   // Define features of which at least one must be supported
       *   data: { features: [Feature.GPS] }
       * }
       * ```
       *
       * The definition of features in the child routes of other modules works the same way but WITHOUT the canLoad member.
       * Example (assume this route is defined in the GPS module and therefore defines the URL "example.com/gps/gps-child"):
       * ```typescript
       * {
       *   path: 'gps-child',
       *   // GPSChild "overwrites" the feature which is defined in the parent route
       *   // This means, the route is only allowed if the active device supports the feature "GPSChild" or "GPSChild2"
       *   data: { titleKey: 'GPS.TITLE', features: [Feature.GPSChild, Feature.GPSChild2] },
       *   component: GpsComponent
       * }
       * ```
       */
      {
        path: 'dashboard',
        loadChildren: () => import('@app/dashboard/dashboard.module').then(m => m.DashboardModule)
      },
      {
        path: 'webfilter',
        loadChildren: () => import('@app/webfilter/webfilter.module').then(m => m.WebfilterModule)
      },
      {
        path: 'web-import-export',
        loadChildren: () => import('@app/webfilter/web-import-export/web-import-export.module').then(m => m.WebImportExportModule)
      },
      {
        path: 'apps',
        loadChildren: () => import('@app/appfilter/appfilter.module').then(m => m.AppfilterModule)
      },
      {
        path: 'reports',
        loadChildren: () => import('@app/reports/reports.module').then(m => m.ReportsModule)
      },
      {
        path: 'account',
        loadChildren: () => import('@app/account/account.module').then(m => m.AccountModule)
      },
      {
        path: 'dev',
        loadChildren: () => import('@app/dev/dev.module').then(m => m.DevModule)
      },
      {
        path: 'debug',
        loadChildren: () => import('@app/debug/debug.module').then(m => m.DebugModule)
      },
      {
        path: 'devicelimit',
        loadChildren: () => import('@app/devicelimit/devicelimit.module').then(m => m.DevicelimitModule)
      },
      {
        path: 'emailreport',
        loadChildren: () => import('@app/emailreport/emailreport.module').then(m => m.EmailreportModule)
      },
      {
        path: 'extension-requests',
        loadChildren: () => import('@app/extensionrequests/extensionrequests.module').then(m => m.ExtensionrequestsModule)
      },
      {
        path: 'gps',
        loadChildren: () => import('@app/gps/gps.module').then(m => m.GpsModule),
        canLoad: [FeatureSupportedGuard],
        data: { features: [Feature.GPS] }
      },
      {
        path: 'notifications',
        loadChildren: () => import('@app/notifications/notifications.module').then(m => m.NotificationsModule)
      },
      {
        path: 'notification-overview',
        loadChildren: () => import('@app/notification-overview/notification-overview.module').then(m => m.NotificationOverviewModule)
      },
      {
        path: 'password',
        loadChildren: () => import('@app/password/password/password.module').then(m => m.PasswordModule)
      },
      {
        path: 'portallog',
        loadChildren: () => import('@app/portallog/portallog.module').then(m => m.PortallogModule)
      },
      {
        path: 'screenshots',
        loadChildren: () => import('@app/screenshots/screenshots.module').then(m => m.ScreenshotsModule),
        canLoad: [FeatureSupportedGuard],
        data: { features: [Feature.Screenshots, Feature.ScreenshotsV2] }
      },
      {
        path: 'security',
        loadChildren: () => import('@app/security/security.module').then(m => m.SecurityModule)
      },
      {
        path: 'sync',
        loadChildren: () => import('@app/sync/sync.module').then(m => m.SyncModule)
      },
      {
        path: 'settings',
        loadChildren: () => import('@app/settings/settings.module').then(m => m.SettingsModule)
      },
      {
        path: 'tans',
        loadChildren: () => import('@app/tans/tans.module').then(m => m.TansModule)
      },
      {
        path: 'app-details',
        loadChildren: () => import('@app/app-details/app-details.module').then(m => m.AppDetailsModule)
      },
      {
        path: 'versionhistory',
        loadChildren: () => import('@app/versionhistory/versionhistory.module').then(m => m.VersionhistoryModule)
      },
      {
        path: 'urgent-notice',
        loadChildren: () => import('@app/urgent-notice/urgent-notice.module').then(m => m.UrgentNoticeModule)
      },
      {
        path: 'whatsnew',
        loadChildren: () => import('@app/whatsnew/whatsnew.module').then(m => m.WhatsnewModule)
      }
    ]
  },
  // All routes that require auth, require the default role and use the DefaultLayoutComponent but don't require a licence
  {
    path: '',
    canActivate: [AuthGuard, RoleAccessGuard, FeatureSupportedGuard],
    canActivateChild: [AuthGuard, RoleAccessGuard, FeatureSupportedGuard],
    canLoad: [FeatureSupportedGuard],
    data: {
      roles: [UserRole.Default, UserRole.Parents]
    },
    component: DefaultLayoutComponent,
    children: [
      {
        path: 'mydata',
        loadChildren: () => import('@app/mydata/mydata.module').then(m => m.MydataModule)
      },
    ]
  },
  // Login uses LoginLayoutComponent
  {
    path: '',
    component: LoginLayoutComponent,
    children: [
      {
        path: 'login',
        component: LoginComponent
      },
      /**
       * The tokenlogin route supports the following query params:
       * - "redirect": Redirects to the given *relative* URL (it does not matter whether a leading '/' is given)
       * - "cb3": Enables AuthService.isCb3
       * - "parents": Sets role to UserRole.Parents
       */
      {
        path: 'tokenlogin/:logintoken',
        component: TokenLoginComponent
      }
    ]
  },
  {
    path: 'logout',
    component: LoginLayoutComponent,
    children: [
      {
        path: '',
        data: { titleKey: 'LOGIN.LOGGED_OUT' },
        loadChildren: () => import('@app/login/logout/logout.module').then(m => m.LogoutModule)
      }
    ]
  },
  {
    path: 'recover-password',
    component: LoginLayoutComponent,
    children: [
      {
        path: '',
        // Add titleKey (language string) or title (plain page title) property to data property to set the title of the page
        // component: RecoverPasswordComponent
        loadChildren: () => import('@app/password/recover-password/recover-password.module').then(m => m.RecoverPasswordModule)
      }
    ]
  },
  {
    path: 'setpassword',
    component: LoginLayoutComponent,
    children: [
      {
        path: '',
        // Add titleKey (language string) or title (plain page title) property to data property to set the title of the page
        // component: RecoverPasswordComponent
        loadChildren: () => import('@app/password/recover-password-change/recover-password-change.module').then(m => m.RecoverPasswordChangeModule)
      }
    ]
  },
  // Routes with AUTH and header, but without Sidebar
  {
    path: 'licence',
    component: NosidebarLayoutComponent,
    canActivate: [AuthGuard, RoleAccessGuard],
    canActivateChild: [AuthGuard, RoleAccessGuard],
    data: {
      roles: [UserRole.Default, UserRole.Parents]
    },
    // Never place LicenceModule in a route with LicenceGuard or a loop will occur!
    loadChildren: () => import('@app/licence/licence.module').then(m => m.LicenceModule)
  },
  {
    path: 'feedback',
    component: NosidebarLayoutComponent,
    canActivate: [AuthGuard, RoleAccessGuard],
    canActivateChild: [AuthGuard, RoleAccessGuard],
    data: {
      roles: [UserRole.Default, UserRole.Parents]
    },
    loadChildren: () => import('@app/feedback/feedback.module').then(m => m.FeedbackModule)
  },
  // Routes with AUTH, but without Sidebar and Header
  {
    path: '',
    component: ParentsLayoutComponent,
    canActivate: [AuthGuard, RoleAccessGuard],
    canActivateChild: [AuthGuard, RoleAccessGuard],
    data: {
      roles: [UserRole.Default, UserRole.Parents]
    },
    children: [
      {
        path: 'parents',
        loadChildren: () => import('@app/parents/parents.module').then(m => m.ParentsModule)
      }
    ]
  },
  {
    path: '',
    component: OptionalHeaderLayoutComponent,
    canActivate: [AuthGuard, RoleAccessGuard],
    canActivateChild: [AuthGuard, RoleAccessGuard],
    data: {
      roles: [UserRole.Default, UserRole.Parents]
    },
    children: [
      {
        path: 'devicenotfound',
        loadChildren: () => import('@app/login/devicenotfound/devicenotfound.module').then(m => m.DevicenotfoundModule)
      }
    ]
  },

  // Userrole report CC -> Routes for readonly-views without sidebar and header
  {
    path: '',
    component: OptionalHeaderLayoutComponent,
    canActivate: [AuthGuard, RoleAccessGuard],
    canActivateChild: [AuthGuard, RoleAccessGuard],
    data: {
      roles: [UserRole.Report, UserRole.Default]
    },
    children: [
      {
        path: 'readonly-report',
        loadChildren: () => import('@app/reports-readonly/reports-readonly.module').then(m => m.ReportsReadonlyModule)
      },
      {
        path: 'readonly-devicelimit',
        loadChildren: () => import('@app/devicelimit-readonly/devicelimit-readonly.module').then(m => m.DevicelimitReadonlyModule)
      }
    ]
  },

  // 404: Redirect to dashbaord. If user is not logged in, this will redirect to login
  { path: '**', redirectTo: '/dashboard' }
];

@NgModule({
  imports: [RouterModule.forRoot(routes, { scrollPositionRestoration: 'enabled', relativeLinkResolution: 'legacy' })],
  exports: [RouterModule]
})
export class AppRoutingModule { }
